import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { Confirm, Modal } from '@components/common';
import Table from '@components/common/Table/Table';

import { Icons } from '@icons/icons';

import { useMutatePortalFiles } from '@hooks/portal/files/useMutatePortalFiles';
import { useQueryFilesMilestonesToLink } from '@hooks/portal/files/useQueryFilesMilestonesToLink';
import { useQueryPortalFiles } from '@hooks/portal/files/useQueryPortalFiles';
import { useQueryPortalFilesMilestones } from '@hooks/portal/files/useQueryPortalFilesMilestones';

import {
	portalFiles as portalFilesSelector,
	portalFolders as portalFoldersSelector,
} from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import { setFolderId, setPage, setPageSize, setSearchValue, setSort } from '@slices/portalFiles';

import { isEmpty } from '@utils/common';

import { IGroupOperationWithActionDTO } from '@interfaces/common/defaultDTO';
import { ISortBy, ITablePagination, ITableSearch, ITableSort } from '@interfaces/components/table';
import { IPortalFolder } from '@interfaces/portal/folders';

import { TMenuActionsTypes } from '@enums/common';

import { MODAL_WIDTH_400, MODAL_WIDTH_480, MODAL_WIDTH_580 } from '@constants/modals';
import { attachFilesTooltip, attachFilesTooltipDisabled, deleteConfirmText, searchTooltipText } from './constants';

import AttachFilesToMilestonesForm from '../AttachFilesToMilestonesForm';
import MoveFilesForm from '../MoveFilesForm';
import UploadFileForm from '../UploadFileForm';
import { getColumns } from './manageFilesColumns';

interface IFilesListTableProps {
	selectedFolder?: IPortalFolder;
	isProjectActive: boolean;
	isPortalAdmin: boolean;
	projectId: string;
	isSingleFolder: boolean;
}

const FilesListTable = ({
	selectedFolder,
	isProjectActive,
	isPortalAdmin,
	projectId,
	isSingleFolder,
}: IFilesListTableProps) => {
	const folderId = selectedFolder?.id;

	const isDeleteAction = isPortalAdmin && isProjectActive;

	const dispatch = useAppDispatch();

	const [isUploadFileFormModal, setIsUploadFileFormModal] = useState(false);
	const [checkedIds, setCheckedIds] = useState<Array<string>>([]);
	const [actionId, setActionId] = useState<TMenuActionsTypes>();
	const [isShowDeleteConfirm, setIsShowDeleteConfirm] = useState(false);
	const [fileIdForRemove, setFileIdForRemove] = useState<string | null>(null);
	const [isMoveFilesModal, setIsMoveFilesModal] = useState(false);
	const [isAttachFilesModal, setIsAttachFilesModal] = useState(false);
	const [selectedFilesIds, setSelectedFilesIds] = useState<string[]>([]);
	const [fileIdForMove, setFileIdForMove] = useState<string | null>(null);
	const [attachFilesActionId, setAttachFilesActionId] = useState<TMenuActionsTypes>();
	const [fileIdForAttach, setFileIdForAttach] = useState<string | null>(null);

	const openDeleteConfirm = useCallback(() => {
		setIsShowDeleteConfirm(true);
	}, []);

	const closeDeleteConfirm = useCallback(() => {
		setIsShowDeleteConfirm(false);
		setFileIdForRemove(null);
	}, []);

	const openUploadFileFormModal = () => {
		setIsUploadFileFormModal(true);
	};

	const closeUploadFileFormModal = () => setIsUploadFileFormModal(false);

	const openMoveFilesModal = () => setIsMoveFilesModal(true);

	const openAttachFilesModal = useCallback(() => {
		setIsAttachFilesModal(true);
	}, []);

	const closeAttachFilesModal = () => {
		setIsAttachFilesModal(false);
		setFileIdForAttach(null);
	};

	const closeMoveFilesModal = () => {
		setIsMoveFilesModal(false);
		setFileIdForMove(null);
	};

	const {
		page,
		pageSize,
		searchValue,
		sortDirection,
		sortFieldName,
	} = useAppSelector(portalFilesSelector);

	const {
		searchValue: globalSearchValue,
	} = useAppSelector(portalFoldersSelector);

	const {
		portalFiles,
		isLoadingPortalFiles,
		countOfRecords,
	} = useQueryPortalFiles();

	const { portalFilesMilestones, isLoadingPortalFilesMilestones } = useQueryPortalFilesMilestones(projectId);
	const {
		portalFilesMilestonesToLink,
		isLoadingPortalFilesMilestonesToLink,
	} = useQueryFilesMilestonesToLink({
		fileId: fileIdForAttach,
		projectId,
	});

	const {
		uploadFiles,
		handleDeletePortalFile,
		handleDeletePortalFiles,
		handleDownloadPortalFiles,
		handleDownloadPortalFile,
		moveFiles,
		handleAttachFilesToMilestones,
		handleAttachFileToMilestones,
		isLoadingPortalDeleteFiles,
	} = useMutatePortalFiles({
		folderId,
		searchValue: globalSearchValue || searchValue,
		closeDeleteConfirm,
	});

	const downloadFile = useCallback((fileId: string) => {
		handleDownloadPortalFile({ fileId });
	}, [handleDownloadPortalFile]);

	const onMoveFileClick = useCallback((id: string) => {
		setFileIdForMove(id);
		openMoveFilesModal();
	}, []);

	const onAttachFileClick = useCallback((id: string) => {
		setFileIdForAttach(id);
		openAttachFilesModal();
	}, [openAttachFilesModal]);

	const onMoveFilesAction = ({ ids }: { ids: string[] }) => {
		if (isEmpty(ids)) {
			return;
		}

		openMoveFilesModal();
	};

	const isAttachDisabled = isEmpty(portalFilesMilestones);

	const columns = useMemo(() => getColumns({
		openDeleteConfirm,
		setFileIdForRemove,
		onAttachFileClick,
		isDeleteAction,
		downloadFile,
		onMoveFileClick,
		isMoveAction: isProjectActive && !isSingleFolder,
		isAttachDisabled,
		isAttachAction: isProjectActive,
	}), [
		isSingleFolder,
		openDeleteConfirm,
		isDeleteAction,
		downloadFile,
		onMoveFileClick,
		isProjectActive,
		onAttachFileClick,
		isAttachDisabled,
	]);

	useEffect(() => {
		dispatch(setFolderId({ folderId: folderId || null }));
	}, [folderId, dispatch]);

	const onSearchHandler = useCallback((searchValue: string) => dispatch(setSearchValue({ searchValue })), [dispatch]);

	const search: Array<ITableSearch> = useMemo(() => [
		{
			initialValue: searchValue,
			name: 'searchValue',
			handler: onSearchHandler,
			tooltipText: searchTooltipText,
			placeholder: searchTooltipText,
		},
	], [searchValue, onSearchHandler]);

	const handleSort = useCallback((sortBy: Array<ISortBy>) => {
		dispatch(setSort({
			sortDirection: !!sortBy[0]?.desc,
			sortFieldName: sortBy[0]?.id,
		}));
	}, [dispatch]);

	const sort: ITableSort = useMemo(() => (
		{
			initialState: [{ id: sortFieldName, desc: sortDirection }],
			handleSort,
		}
	), [sortFieldName, sortDirection, handleSort]);

	const pageHandler = useCallback((page: number) => dispatch(setPage({ page })), [dispatch]);

	const pageSizeHandler = useCallback((pageSize: number) => dispatch(setPageSize({ pageSize })), [dispatch]);

	const pagination: ITablePagination = useMemo(() => (
		{
			countOfRecords,
			forcePage: page,
			size: pageSize,
			pageHandler,
			pageSizeHandler,
		}
	), [
		countOfRecords,
		page,
		pageSize,
		pageHandler,
		pageSizeHandler,
	]);

	const handleCustomDelete = useCallback((data: IGroupOperationWithActionDTO) => {
		openDeleteConfirm();
		setCheckedIds(data.ids);
		setActionId(data.actionType);
	}, [openDeleteConfirm]);

	const handleAttachFiles = useCallback((data: IGroupOperationWithActionDTO) => {
		openAttachFilesModal();
		setSelectedFilesIds(data.ids);
		setAttachFilesActionId(data.actionType);
	}, [openAttachFilesModal]);

	const handleExport = useCallback((ids: Array<string>, actionType: TMenuActionsTypes) => {
		return handleDownloadPortalFiles({ ids, actionType });
	}, [handleDownloadPortalFiles]);

	const deleteItemsHandler = useCallback(() => {
		if (fileIdForRemove) {
			return handleDeletePortalFile(fileIdForRemove);
		}

		return handleDeletePortalFiles({ ids: checkedIds, actionType: actionId });
	}, [
		actionId,
		checkedIds,
		handleDeletePortalFile,
		handleDeletePortalFiles,
		fileIdForRemove,
	]);

	useEffect(() => {
		if (globalSearchValue && searchValue) {
			onSearchHandler('');
		}
	}, [globalSearchValue, searchValue, onSearchHandler]);

	const portalFilesList = folderId ? portalFiles : [];

	const milestoneOptions = fileIdForAttach
		? portalFilesMilestonesToLink
		: portalFilesMilestones.map(milestone => {
			return {
				...milestone,
				checked: false,
			};
		});

	return (
		<>
			<Table
				addIcon={Icons.import}
				addText='Upload files'
				attachTooltipText={isAttachDisabled ? attachFilesTooltipDisabled : attachFilesTooltip}
				columns={columns}
				data={portalFilesList}
				handleAttach={handleAttachFiles}
				handleCustomDelete={handleCustomDelete}
				handleExport={handleExport}
				handleMoveItems={onMoveFilesAction}
				isAttach={isProjectActive}
				isAttachDisabled={isAttachDisabled}
				isCreatable={isProjectActive}
				isCustomActionInGroupOperations
				isExport
				isLoading={isLoadingPortalFiles || isLoadingPortalFilesMilestones}
				isMoveItems={isProjectActive && !isSingleFolder}
				isSearchDisabled={!!globalSearchValue}
				isWithoutDeleteAction={!isDeleteAction}
				isWithoutFilter
				maxHeight='55vh'
				moveItemsTooltipText='Move files'
				pagination={pagination}
				search={search}
				searchThreshold={0}
				sort={sort}
				toggleAddModal={openUploadFileFormModal}
			/>
			{selectedFolder
				&& <Modal
					isOpen={isUploadFileFormModal}
					maxHeight='750px'
					onClose={closeUploadFileFormModal}
					title='Upload file'
					width='600px'
				>
					<UploadFileForm
						closeUploadFileForm={closeUploadFileFormModal}
						projectId={projectId}
						selectedFolder={selectedFolder}
						uploadFiles={uploadFiles}
					/>
				</Modal>
			}
			<Modal
				isOpen={isMoveFilesModal}
				onClose={closeMoveFilesModal}
				title='Move files'
				width={MODAL_WIDTH_580}
			>
				<MoveFilesForm
					closeMoveFilesForm={closeMoveFilesModal}
					filesIdsForMove={fileIdForMove ? [fileIdForMove] : selectedFilesIds}
					handleMoveFiles={moveFiles}
					projectId={projectId}
					selectedFolder={selectedFolder}
				/>
			</Modal>
			<Modal
				isOpen={isAttachFilesModal}
				onClose={closeAttachFilesModal}
				title='Select milestone'
				width={MODAL_WIDTH_400}
			>
				<AttachFilesToMilestonesForm
					attachFilesActionId={attachFilesActionId}
					closeAttachFilesForm={closeAttachFilesModal}
					handleAttachFileToMilestones={handleAttachFileToMilestones}
					handleAttachFilesToMilestones={handleAttachFilesToMilestones}
					isLoading={isLoadingPortalFilesMilestonesToLink}
					milestoneOptions={milestoneOptions}
					selectedFilesIds={selectedFilesIds}
					singleFileIdForAttach={fileIdForAttach}
				/>
			</Modal>
			<Modal
				isOpen={isShowDeleteConfirm}
				onClose={closeDeleteConfirm}
				title='Confirm action'
				width={MODAL_WIDTH_480}
			>
				<Confirm
					isLoading={isLoadingPortalDeleteFiles}
					onSubmit={deleteItemsHandler}
					question={deleteConfirmText}
				/>
			</Modal>
		</>
	);
};

export default memo(FilesListTable);
